import type { FC, ReactNode } from 'react'
import type { Post } from '@/lib/posts'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { formatPostDate } from '@/lib/dates'
import { PostWindow } from '@/components/blog/post-cover'
import { routes } from '@/constants/site'

export const postHref = (slug: string): string => `${routes.blog}/${slug}`

interface PostMetaProps {
    post: Post
    className?: string
}

/** "1 Oct 2026 · Hiring" */
export const PostMeta: FC<PostMetaProps> = ({ post, className }): ReactNode => {
    return (
        <p className={cn('flex gap-2 text-[15px] leading-5 tracking-[-0.01em] text-ink/50', className)}>
            <time dateTime={post.publishedAt}>{formatPostDate(post.publishedAt)}</time>
            <span aria-hidden='true' className='text-ink/35'>·</span>
            <span>{post.category}</span>
        </p>
    )
}

const CARD = 'card-frame group relative transition-transform duration-200 ease-out hover:-translate-y-0.5'
const TITLE = 'font-serif text-[26.7px] leading-[34px] tracking-[-0.024em] text-ink max-[700px]:text-[24px] max-[700px]:leading-[31px]'
const DEK = 'line-clamp-3 text-[16.5px] leading-[25px] tracking-[-0.01em] text-ink/[0.68]'

interface PostCardProps {
    post: Post
    className?: string
}

/**
 * A card in the "All articles" grid: the cover window, the meta line, the title and the dek.
 * Cards in a row share the grid's row tracks, so every dek starts on the same line.
 */
export const PostGridCard: FC<PostCardProps> = ({ post, className }): ReactNode => {
    return (
        <article className={cn(CARD, 'row-span-4 grid grid-rows-subgrid content-start gap-y-0', className)}>
            <PostWindow post={post} className='aspect-[417/249]' />
            <PostMeta post={post} className='mt-5 px-3' />
            <h3 className={cn(TITLE, 'mt-[13px] min-h-[2lh] px-3')}>
                <Link href={postHref(post.slug)} className='after:absolute after:inset-0 after:z-10 after:rounded-[16px] after:content-[""] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-ink/75'>
                    {post.title}
                </Link>
            </h3>
            <p className={cn(DEK, 'mb-3 mt-[13px] px-3')}>
                {post.excerpt}
            </p>
        </article>
    )
}

/** The same card anatomy outside a shared grid, for "Keep reading". */
export const PostCard: FC<PostCardProps> = ({ post, className }): ReactNode => {
    return (
        <article className={cn(CARD, 'flex flex-col', className)}>
            <PostWindow post={post} className='aspect-[417/249]' />
            <PostMeta post={post} className='mt-5 px-3' />
            <h3 className={cn(TITLE, 'mt-[13px] px-3')}>
                <Link href={postHref(post.slug)} className='after:absolute after:inset-0 after:z-10 after:rounded-[16px] after:content-[""] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-ink/75'>
                    {post.title}
                </Link>
            </h3>
            <p className={cn(DEK, 'mb-3 mt-[13px] px-3')}>
                {post.excerpt}
            </p>
        </article>
    )
}
